import React, { useState, useEffect } from 'react';
import { Modal, Banner } from '@shopify/polaris';
import Papa from 'papaparse';
import { api } from './apiClient';

const REQUIRED_HEADERS = ['Store', 'SKU', 'Min Price', 'Max Price'];

function normalizeHeader(h) {
    return String(h || '').trim().toLowerCase();
}

function findHeaderIndex(headers, target) {
    const t = normalizeHeader(target);
    return headers.findIndex(h => normalizeHeader(h) === t);
}

function parseMoneyCell(s) {
    const t = String(s ?? '').trim();
    if (!t) return { blank: true, value: null };
    const cleaned = t.replace(/[$,\s]/g, '');
    const n = parseFloat(cleaned);
    if (!Number.isFinite(n)) return { blank: false, value: null, invalid: true };
    return { blank: false, value: n };
}

/**
 * Classify each parsed row into change / unchanged / skipped.
 * `productByKey` is a Map keyed by `${store_id}|${sku}` containing rows the
 * backend confirmed exist; pairs missing from the map are SKUs the server
 * couldn't find for that store (skipped, not silent-unchanged).
 *
 * Returns { changes, unchangedCount, skipped, fileError }.
 */
export function classifyRows(parsedRows, productByKey, stores) {
    const result = { changes: [], unchangedCount: 0, skipped: [], fileError: null };

    if (!parsedRows.length) {
        result.fileError = 'CSV is empty.';
        return result;
    }

    const headers = parsedRows[0];
    const idxStore = findHeaderIndex(headers, 'Store');
    const idxSku = findHeaderIndex(headers, 'SKU');
    const idxMin = findHeaderIndex(headers, 'Min Price');
    const idxMax = findHeaderIndex(headers, 'Max Price');
    const missing = REQUIRED_HEADERS.filter((h, i) =>
        [idxStore, idxSku, idxMin, idxMax][i] === -1);
    if (missing.length) {
        result.fileError = `Missing required column(s): ${missing.join(', ')}`;
        return result;
    }

    const storeByName = new Map();
    for (const s of stores) {
        storeByName.set(normalizeHeader(s.display_name), s.id);
    }

    const seenKeys = new Map();
    const dupes = [];

    for (let i = 1; i < parsedRows.length; i++) {
        const row = parsedRows[i];
        if (!row || row.every(c => String(c ?? '').trim() === '')) continue;

        const storeName = String(row[idxStore] ?? '').trim();
        const sku = String(row[idxSku] ?? '').trim();
        const minCell = parseMoneyCell(row[idxMin]);
        const maxCell = parseMoneyCell(row[idxMax]);

        if (!storeName || !sku) {
            result.skipped.push({ rowNum: i + 1, store: storeName, sku, reason: 'missing Store or SKU' });
            continue;
        }

        const sid = storeByName.get(normalizeHeader(storeName));
        if (sid === undefined) {
            result.skipped.push({ rowNum: i + 1, store: storeName, sku, reason: 'unknown store' });
            continue;
        }

        const dupKey = `${sid}|${sku}`;
        if (seenKeys.has(dupKey)) dupes.push({ store: storeName, sku, firstRow: seenKeys.get(dupKey) + 1, dupRow: i + 1 });
        else seenKeys.set(dupKey, i);

        const product = productByKey.get(dupKey);
        if (!product) {
            // The backend lookup confirmed this (store, sku) doesn't exist.
            result.skipped.push({ rowNum: i + 1, store: storeName, sku, reason: 'SKU not found in this store' });
            continue;
        }

        if (minCell.invalid || maxCell.invalid) {
            result.skipped.push({ rowNum: i + 1, store: storeName, sku, reason: 'non-numeric min or max' });
            continue;
        }

        if (minCell.blank && maxCell.blank) {
            result.unchangedCount += 1;
            continue;
        }

        const effMin = minCell.blank ? Number(product.price_min) : minCell.value;
        const effMax = maxCell.blank ? Number(product.price_max) : maxCell.value;

        if (Number.isNaN(effMin) || Number.isNaN(effMax)) {
            result.skipped.push({ rowNum: i + 1, store: storeName, sku, reason: 'min and max must be set' });
            continue;
        }
        if (effMin < 0 || effMax < 0) {
            result.skipped.push({ rowNum: i + 1, store: storeName, sku, reason: 'negative price' });
            continue;
        }
        if (effMin > effMax) {
            result.skipped.push({ rowNum: i + 1, store: storeName, sku, reason: 'min > max' });
            continue;
        }

        const curMin = product.price_min == null ? null : Number(product.price_min);
        const curMax = product.price_max == null ? null : Number(product.price_max);
        const minChanged = !minCell.blank && (curMin == null || Math.abs(effMin - curMin) >= 0.01);
        const maxChanged = !maxCell.blank && (curMax == null || Math.abs(effMax - curMax) >= 0.01);

        if (!minChanged && !maxChanged) {
            result.unchangedCount += 1;
            continue;
        }

        result.changes.push({
            store_id: sid,
            store_name: storeName,
            sku,
            title: product.title || '',
            current_price: product.my_price,
            prev_min: curMin,
            prev_max: curMax,
            new_min: minChanged ? effMin : null,
            new_max: maxChanged ? effMax : null,
            min_blank_in_upload: minCell.blank,
            max_blank_in_upload: maxCell.blank,
        });
    }

    if (dupes.length) {
        result.fileError = `Duplicate (Store, SKU) rows: ${dupes.slice(0, 5).map(d => `${d.store}/${d.sku} (rows ${d.firstRow}, ${d.dupRow})`).join('; ')}${dupes.length > 5 ? '…' : ''}`;
    }

    return result;
}

function DiffCell({ prev, next, blank }) {
    if (next == null) {
        // Unchanged in this row
        const display = prev == null ? '—' : Number(prev).toFixed(2);
        return <span style={{ color: '#666' }}>
            {display}
            {blank && <span title="Blank in upload — value left unchanged" style={{ color: '#999', marginLeft: 4 }}>·</span>}
        </span>;
    }
    return (
        <span>
            <s style={{ color: '#d72c0d' }}>{prev == null ? '—' : Number(prev).toFixed(2)}</s>
            {' → '}
            <strong style={{ color: '#008060' }}>{Number(next).toFixed(2)}</strong>
        </span>
    );
}

export default function CsvImportModal({ file, stores, onClose, onApplied }) {
    const [parsed, setParsed] = useState(null);
    const [parseErr, setParseErr] = useState(null);
    const [applying, setApplying] = useState(false);
    const [applyResult, setApplyResult] = useState(null);
    const [applyError, setApplyError] = useState(null);
    const [skippedExpanded, setSkippedExpanded] = useState(false);

    useEffect(() => {
        if (!file) return;
        let cancelled = false;
        setParsed(null);
        setParseErr(null);

        Papa.parse(file, {
            header: false,
            skipEmptyLines: true,
            complete: async (results) => {
                if (cancelled) return;
                if (results.errors && results.errors.length) {
                    setParseErr(`CSV parse error: ${results.errors[0].message}`);
                    return;
                }
                const rows = results.data;

                const storeByName = new Map();
                for (const s of stores) {
                    storeByName.set(String(s.display_name || '').trim().toLowerCase(), s.id);
                }
                const pairs = [];
                const seen = new Set();
                if (rows.length > 0) {
                    const headers = rows[0];
                    const sIdx = headers.findIndex(h => String(h || '').trim().toLowerCase() === 'store');
                    const kIdx = headers.findIndex(h => String(h || '').trim().toLowerCase() === 'sku');
                    if (sIdx !== -1 && kIdx !== -1) {
                        for (let i = 1; i < rows.length; i++) {
                            const r = rows[i];
                            if (!r) continue;
                            const sn = String(r[sIdx] ?? '').trim();
                            const sk = String(r[kIdx] ?? '').trim();
                            if (!sn || !sk) continue;
                            const sid = storeByName.get(sn.toLowerCase());
                            if (sid === undefined) continue;
                            const key = `${sid}|${sk}`;
                            if (seen.has(key)) continue;
                            seen.add(key);
                            pairs.push({ store_id: sid, sku: sk });
                        }
                    }
                }

                let productByKey = new Map();
                if (pairs.length > 0) {
                    try {
                        const body = await api.lookup(pairs);
                        for (const p of body.products) {
                            productByKey.set(`${p.store_id}|${p.sku}`, p);
                        }
                    } catch (e) {
                        if (cancelled) return;
                        setParseErr(`Couldn't fetch product details: ${e.message || String(e)}`);
                        return;
                    }
                }

                if (cancelled) return;
                const cls = classifyRows(rows, productByKey, stores);
                setParsed(cls);
            },
            error: (err) => { if (!cancelled) setParseErr(`CSV parse error: ${err.message || String(err)}`); },
        });

        return () => { cancelled = true; };
    }, [file, stores]);

    const handleApply = async () => {
        if (!parsed) return;
        setApplying(true);
        setApplyError(null);
        try {
            const payload = {
                changes: parsed.changes.map(c => ({
                    store_id: c.store_id,
                    sku: c.sku,
                    price_min: c.min_blank_in_upload ? null : c.new_min,
                    price_max: c.max_blank_in_upload ? null : c.new_max,
                })),
            };
            const body = await api.repricerBulkUpdate(payload.changes);
            setApplyResult(body);
            // Always lift partial-or-full successes to parent so the table reflects them.
            // The modal closes itself only when the user clicks Close (after success or
            // after reviewing failures).
            const okSet = new Set(
                body.results.filter(r => r.status === 'ok').map(r => `${r.store_id}|${r.sku}`)
            );
            const updates = parsed.changes
                .filter(c => okSet.has(`${c.store_id}|${c.sku}`))
                .map(c => ({
                    store_id: c.store_id,
                    sku: c.sku,
                    price_min: c.min_blank_in_upload ? c.prev_min : c.new_min,
                    price_max: c.max_blank_in_upload ? c.prev_max : c.new_max,
                }));
            if (updates.length > 0) onApplied(updates);
        } catch (e) {
            setApplyError(e.message || String(e));
        }
        setApplying(false);
    };

    const handleRetryFailed = () => {
        if (!applyResult) return;
        const failedKeys = new Set(
            applyResult.results.filter(r => r.status === 'error').map(r => `${r.store_id}|${r.sku}`)
        );
        const remaining = parsed.changes.filter(c => failedKeys.has(`${c.store_id}|${c.sku}`));
        setParsed({ ...parsed, changes: remaining });
        setApplyResult(null);
        setApplyError(null);
    };

    // Determine modal content state
    let body;
    let primaryAction = null;
    let secondaryActions = [{ content: 'Cancel', onAction: onClose }];

    if (parseErr) {
        body = <Banner tone="critical">{parseErr}</Banner>;
    } else if (!parsed) {
        body = <div style={{ padding: 16 }}>Parsing CSV…</div>;
    } else if (parsed.fileError) {
        body = <Banner tone="critical">{parsed.fileError}</Banner>;
    } else {
        const errorMap = applyResult ? new Map(
            applyResult.results.filter(r => r.status === 'error').map(r => [`${r.store_id}|${r.sku}`, r.detail])
        ) : null;
        const okMap = applyResult ? new Set(
            applyResult.results.filter(r => r.status === 'ok').map(r => `${r.store_id}|${r.sku}`)
        ) : null;
        // Ok rows carry a detail only when the server saved locally without pushing to Repricer.
        const noteMap = applyResult ? new Map(
            applyResult.results.filter(r => r.detail && r.detail.includes('pushes are off'))
                .map(r => [`${r.store_id}|${r.sku}`, r.detail])
        ) : null;

        body = (
            <div>
                {applyError && <Banner tone="critical">Push failed — no changes were applied. {applyError}</Banner>}
                {applyResult && applyResult.failed > 0 && (
                    <Banner tone="warning">
                        {applyResult.applied} applied, {applyResult.failed} failed.
                    </Banner>
                )}
                {applyResult && applyResult.failed === 0 && (
                    <Banner tone="success">All {applyResult.applied} changes applied.</Banner>
                )}
                <div style={{ marginTop: 12, fontSize: 13, color: '#444' }}>
                    {parsed.changes.length} change(s) ready to apply
                </div>
                <div style={{ marginTop: 8, maxHeight: 360, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                            <tr>
                                <th style={{ padding: 6, textAlign: 'left', width: 24 }}></th>
                                <th style={{ padding: 6, textAlign: 'left' }}>Store</th>
                                <th style={{ padding: 6, textAlign: 'left' }}>SKU</th>
                                <th style={{ padding: 6, textAlign: 'left' }}>Title</th>
                                <th style={{ padding: 6, textAlign: 'right' }}>Cur. Price</th>
                                <th style={{ padding: 6, textAlign: 'right' }}>Min</th>
                                <th style={{ padding: 6, textAlign: 'right' }}>Max</th>
                            </tr>
                        </thead>
                        <tbody>
                            {parsed.changes.map((c, i) => {
                                const key = `${c.store_id}|${c.sku}`;
                                const errDetail = errorMap?.get(key);
                                const isOk = okMap?.has(key);
                                const note = noteMap?.get(key);
                                return (
                                    <tr key={i} style={{ borderTop: '1px solid #eee', background: errDetail ? '#fff7f5' : (isOk ? '#f4faf6' : 'transparent') }}>
                                        <td style={{ padding: 6, textAlign: 'center' }}>
                                            {isOk && <span style={{ color: '#008060' }}>✓</span>}
                                            {note && <div style={{ fontSize: 11, color: '#8a6d00', whiteSpace: 'nowrap' }}>{note}</div>}
                                            {errDetail && <span title={errDetail} style={{ color: '#d72c0d', cursor: 'help' }}>✗</span>}
                                        </td>
                                        <td style={{ padding: 6 }}>{c.store_name}</td>
                                        <td style={{ padding: 6, fontFamily: 'monospace' }}>{c.sku}</td>
                                        <td style={{ padding: 6, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.title}>{c.title}</td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>{c.current_price != null ? `$${Number(c.current_price).toFixed(2)}` : '—'}</td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>
                                            <DiffCell prev={c.prev_min} next={c.new_min} blank={c.min_blank_in_upload} />
                                        </td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>
                                            <DiffCell prev={c.prev_max} next={c.new_max} blank={c.max_blank_in_upload} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {parsed.skipped.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <button
                            onClick={() => setSkippedExpanded(v => !v)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#5c6ac4', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'left' }}
                        >
                            {skippedExpanded ? '▾' : '▸'} Skipped ({parsed.skipped.length})
                            {(() => {
                                const counts = {};
                                for (const s of parsed.skipped) counts[s.reason] = (counts[s.reason] || 0) + 1;
                                const parts = Object.entries(counts).map(([r, n]) => `${n} ${r}`);
                                return ` — ${parts.join(', ')}`;
                            })()}
                            <span style={{ color: '#999', fontWeight: 400, marginLeft: 6 }}>(click to {skippedExpanded ? 'collapse' : 'expand'})</span>
                        </button>
                        {skippedExpanded && (
                            <ul style={{ marginTop: 6, fontSize: 12, color: '#666', maxHeight: 200, overflowY: 'auto' }}>
                                {parsed.skipped.slice(0, 50).map((s, i) => (
                                    <li key={i}>Row {s.rowNum}: Store=<code>{s.store}</code> SKU=<code>{s.sku}</code> — {s.reason}</li>
                                ))}
                                {parsed.skipped.length > 50 && (
                                    <li style={{ color: '#999' }}>… and {parsed.skipped.length - 50} more</li>
                                )}
                            </ul>
                        )}
                    </div>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                    Unchanged: {parsed.unchangedCount} row(s)
                </div>
            </div>
        );

        if (applyResult && applyResult.failed > 0) {
            primaryAction = { content: `Retry failed (${applyResult.failed})`, onAction: handleRetryFailed };
            secondaryActions = [{ content: 'Close', onAction: onClose }];
        } else if (applyResult && applyResult.failed === 0) {
            primaryAction = { content: 'Close', onAction: onClose };
            secondaryActions = [];
        } else {
            primaryAction = {
                content: applying ? 'Pushing…' : `Apply ${parsed.changes.length} change(s)`,
                onAction: handleApply,
                disabled: applying || parsed.changes.length === 0,
                loading: applying,
            };
        }
    }

    return (
        <Modal
            open
            onClose={onClose}
            title="Review pricing changes"
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
            size="large"
        >
            <Modal.Section>{body}</Modal.Section>
        </Modal>
    );
}
